import type { DefaultOptionType } from 'rc-select/lib/Select';
import React from 'react';

import CallCard from '~/components/Calls/CallCard';
import Boolean from '~/components/Columns/Boolean';
import DateOnly from '~/components/Columns/Date';
import DateTime from '~/components/Columns/DateTime';
import PhoneNumber from '~/components/Columns/PhoneNumber';
import { prefetchOptions, toLabel } from '~/components/Columns/helpers';

import {
  type CollectionColumnDefinition,
  type ColumnDefinition,
  DateFilterPresets,
  ModelColumns,
} from '~/types/components';
import type { Call, Campaign, Contact, Gender, Group, PointageResult, Tag, WorkStatus } from '~/types/models';
import { genderLabel, languageLabel, pointageResultLabel, workStatusLabel } from '~/types/models';

import { labelsToOptions } from '~/helpers';

const contactCallsColumn: CollectionColumnDefinition<Contact, Call> = {
  title: 'Appels',
  key: 'calls',
  only: ['single'],
  sortable: false,
  type: 'collection',
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }
    if (value.length === 0) {
      return <em>Aucun appel</em>;
    }
    return (
      <div>
        {value.filter((c) => !!c).map((c) => (
          <div key={c.id} style={{ marginBottom: 10 }}>
            <CallCard call={c} />
          </div>
        ))}
      </div>
    );
  },
  with: 'calls,calls.group,calls.campaign,calls.tags',
};

const contactCampaignsColumn: CollectionColumnDefinition<Contact, Campaign> = {
  title: 'Campagnes',
  key: 'campaigns',
  mapOptions(items: Campaign[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<Campaign>('campaigns'),
  sortable: false,
  type: 'collection',
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return <em>Aucune campagne</em>;
    }

    return (
      <ul style={{ margin: 0, padding: 0 }}>
        {value.map((g) => (
          <li key={g.id}>{g.name}</li>
        ))}
      </ul>
    );
  },
  toLabel: toLabel('campaigns', 'Campagne'),
};

const contactGroupsColumn: CollectionColumnDefinition<Contact, Group> = {
  title: 'Groupes',
  key: 'groups',
  mapOptions(items: Group[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<Group>('groups', [
    {
      id: '¤',
      name: 'Orphelin',
      callsCount: 0,
      canLeaveMessage: false,
      contactsCount: 0,
      created: '',
      description: '',
      humanId: '',
      lastUpdated: '',
      meta: {},
      script: '',
      sequence: 0,
    },
  ]),
  sortable: false,
  toLabel: toLabel('groups', 'Groupe', 'Orphelin'),
  type: 'collection',
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }
    if (value.length === 0) {
      return <em>Aucun groupe</em>;
    }
    return (
      <ul style={{ margin: 0, padding: 0 }}>
        {value.map((g) => (
          <li key={g.id}>{g.name}</li>
        ))}
      </ul>
    );
  },
};

const contactTagsColumn: CollectionColumnDefinition<Contact, Tag> = {
  title: 'Étiquettes',
  key: 'tags',
  mapOptions(items: Tag[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<Tag>('tags'),
  sortable: false,
  toLabel: toLabel('tags', 'Étiquette'),
  type: 'collection',
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return <em>Aucune étiquette</em>;
    }

    return (
      <ul style={{ margin: 0, padding: 0 }}>
        {value.map((g) => (
          <li key={g.id}>{g.name}</li>
        ))}
      </ul>
    );
  },
};

export const Columns: Array<ColumnDefinition<Contact>> = [
  {
    title: 'Adresse',
    key: 'address',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Commentaires sur les Appels',
    filterable: false,
    key: 'callComments',
    readOnly: true,
    render: (v) => <span style={{ whiteSpace: 'break-spaces' }}>{v}</span>,
    sortable: false,
    type: 'string',
    with: 'calls,calls.campaign,calls.group',
  },
  {
    title: 'Réponses aux textos',
    filterable: false,
    key: 'smsAnswers',
    readOnly: true,
    render: (v) => <span style={{ whiteSpace: 'break-spaces' }}>{v}</span>,
    sortable: false,
    type: 'string',
    with: 'sms,sms.campaign',
  },
  {
    title: 'Catégorie',
    key: 'category',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Téléphone (cellulaire)',
    key: 'cellPhoneNumber',
    mask: '(###) ###-####',
    render: (v) => <PhoneNumber value={v} />,
    sortable: true,
    type: 'string',
  },
  {
    title: 'Ville',
    key: 'city',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Commentaire',
    key: 'comment',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Courriel',
    key: 'email',
    sortable: true,
    type: 'string',
  },
  {
    title: "Numéro d'employé",
    key: 'employeeNumber',
    sortable: true,
    type: 'string',
  },
  {
    title: "Courriel de l'employeur",
    key: 'employerEmail',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Employeur',
    key: 'employerName',
    sortable: true,
    type: 'string',
  },
  {
    title: "Date d'embauche",
    key: 'employmentStart',
    presets: DateFilterPresets.Past,
    sortable: true,
    type: 'date',
    render: (v) => <DateOnly value={v} />,
  },
  {
    title: 'Fax',
    key: 'faxPhoneNumber',
    mask: '(###) ###-####',
    sortable: true,
    type: 'string',
    render: (v) => <PhoneNumber value={v} />,
  },
  {
    title: 'Prénom',
    key: 'firstName',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Sexe',
    key: 'gender',
    options: labelsToOptions(genderLabel),
    render: (value: Gender) => <span>{genderLabel[value] ?? 's. o.'}</span>,
    sortable: false,
    type: 'enum',
  },
  {
    title: 'Courriel valide?',
    key: 'hasValidEmail',
    labelFalse: 'Courriel invalide',
    labelTrue: 'Courriel valide',
    sortable: true,
    type: 'boolean',
    readOnly: true,
    render: (value: boolean) => <Boolean value={value} />,
  },
  {
    title: 'Numéro de téléphone valide?',
    key: 'hasValidPhoneNumber',
    labelFalse: 'Aucun numéro valide',
    labelTrue: 'Au moins un numéro valide',
    sortable: true,
    type: 'boolean',
    readOnly: true,
    render: (value: boolean) => <Boolean value={value} />,
  },
  {
    title: 'A voté',
    hint: 'Cette colonne est contextuelle en fonction du groupe courant ou du filtre de groupe appliqué.',
    key: 'hasVoted',
    labelTrue: 'A voté',
    labelFalse: "N'a pas voté",
    sortable: false,
    type: 'boolean',
    render: (value: boolean) => <Boolean value={value} />,
    with: 'contactgroups',
  },
  {
    title: 'Téléphone (maison)',
    key: 'homePhoneNumber',
    mask: '(###) ###-####',
    sortable: true,
    type: 'string',
    render: (v) => <PhoneNumber value={v} />,
  },
  {
    title: "Syndicat d'origine",
    key: 'initialUnionName',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Langue',
    key: 'language',
    options: labelsToOptions(languageLabel),
    sortable: true,
    type: 'enum',
  },
  {
    title: 'Dernier appel',
    key: 'lastCalled',
    nullable: 'Jamais appelé',
    presets: DateFilterPresets.Past,
    readOnly: true,
    sortable: true,
    type: 'datetime',
    render: (v) => <DateTime value={v} />,
  },
  {
    title: 'Adhésion à la CSN la plus récente',
    key: 'lastMembershipStart',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Nom',
    key: 'lastName',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Dernier pointage',
    key: 'lastResult',
    options: labelsToOptions(pointageResultLabel),
    readOnly: true,
    render: (value: PointageResult) => <span>{pointageResultLabel[value] ?? 's. o.'}</span>,
    sortable: true,
    type: 'enum',
  },
  {
    title: 'Code postal',
    key: 'postalCode',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Adresse précédente',
    key: 'previousAddress',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Début de la précédente adhésion',
    key: 'previousMembershipStart',
    presets: DateFilterPresets.Past,
    sortable: true,
    type: 'date',
    render: (v) => <DateOnly value={v} />,
  },
  {
    title: 'Province',
    key: 'province',
    options: [], // TODO
    sortable: true,
    type: 'enum',
  },
  {
    title: 'Référant',
    key: 'referrer',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Région',
    key: 'region',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Service',
    key: 'service',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Site',
    key: 'site',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Nom du syndicat',
    key: 'unionName',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Inscription aux courriels',
    key: 'unsubscribedFromEmails',
    labelTrue: 'Désinscrit des courriels',
    labelFalse: 'Inscrit aux courriels',
    sortable: true,
    type: 'boolean',
    readOnly: true,
    render: (value: boolean) => <Boolean value={!value} />,
  },
  {
    title: 'Inscription aux textos',
    key: 'unsubscribedFromSms',
    labelTrue: 'Désinscrit des textos',
    labelFalse: 'Inscrit aux textos',
    sortable: true,
    type: 'boolean',
    readOnly: true,
    render: (value: boolean) => <Boolean value={!value} />,
  },
  {
    title: 'Bonne carte',
    key: 'validCard',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Département',
    key: 'workDepartment',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Téléphone (travail)',
    key: 'workPhoneNumber',
    mask: '(###) ###-####',
    sortable: true,
    type: 'string',
    render: (v) => <PhoneNumber value={v} />,
  },
  {
    title: 'Quart de travail',
    key: 'workShift',
    sortable: true,
    type: 'string',
  },
  {
    title: "Statut d'emploi",
    key: 'workStatus',
    options: labelsToOptions(workStatusLabel, 'Non spécifié'),
    render: (value: WorkStatus) => <span>{workStatusLabel[value] ?? 's. o.'}</span>,
    sortable: true,
    type: 'enum',
  },
  {
    title: "Titre d'emploi",
    key: 'workTitle',
    sortable: true,
    type: 'string',
  },
  {
    title: "No titre d'emploi",
    key: 'workTitleNumber',
    sortable: true,
    type: 'string',
  },
  contactCallsColumn,
  contactCampaignsColumn,
  contactGroupsColumn,
  contactTagsColumn,
  ...ModelColumns,
];
